
/* eslint-disable @typescript-eslint/camelcase */
import { Options, Vue, prop } from "vue-class-component";
import SelectSearch from "@/app/ui/components/select-search/index.vue";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";
import MultipleSelectSearch from "@/app/ui/components/multiple-select-search/index.vue";
import BaseModal from "@/app/ui/components/modal/index.vue";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import { TransferPickupRequest } from "@/domain/entities/PickupDashboard";
import { OptionsClass } from "@/domain/entities/MainApp";
import { debounce } from "lodash";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";

interface SelectedSellerData {
  id: string;
  sellerName: string;
  sellerPhone: string;
  packageCount: number;
  koli: number;
  weight: number;
  taskGroupId?: number;
  index: number;
}

class Props {
  show = prop<boolean>({ required: true });
  selectedSellers = prop<SelectedSellerData[]>({ required: true });
  sourceCourierId = prop<number>({ required: true });
}

@Options({
  name: "TransferPickupModal",
  components: {
    SelectSearch,
    MultipleSelectSearch,
    BaseModal
  },
  emits: ["update:show", "remove-seller", "transfer-success"]
})
class TransferPickupModal extends Vue.with(Props) {
  formatPhoneNumber = formatPhoneNumber;

  waNumber(phone: string) {
    return String(phone || "").replace(/\D/g, "");
  }

  selectedCourier: any = "";
  selectedShipmentTypes: OptionsClass[] = [];
  isAssigning = false;
  isShowModalSuccessBooking = false;
  isShowModalErrorBooking = false;
  openPartial = false; // For partial success modal
  openModalAllError = false;
  transferResult: any = null; // Store transfer result data
  errorAssignData: any[] = []; // Store failed assignments data
  errorAllAssignData: any[] = [];

  get selectedCourierName() {
    return String(
      this.selectedCourier?.name?.split("] ")[1]?.split(" (")[0] || ""
    );
  }

  shipmentTypes = [
    new OptionsClass({
      name: "AGGREGATOR",
      value: "AGGREGATOR"
    }),
    new OptionsClass({
      name: "MARKETPLACE",
      value: "MARKETPLACE"
    }),
    new OptionsClass({
      name: "CUSTOMER APP",
      value: "CUSTOMER_APP"
    }),
    new OptionsClass({
      name: "CORPORATE",
      value: "CORPORATE"
    })
  ];

  get isSingleSeller(): boolean {
    const sellers = this.selectedSellers || [];
    return sellers.length === 1;
  }

  get totalSenders(): number {
    const sellers = this.selectedSellers || [];
    return sellers.length;
  }

  get totalPackages(): number {
    const sellers = this.selectedSellers || [];
    return sellers.reduce((sum, seller) => sum + seller.packageCount, 0);
  }

  get totalKoli(): number {
    const sellers = this.selectedSellers || [];
    return sellers.reduce((sum, seller) => sum + seller.koli, 0);
  }

  get totalWeight(): number {
    const sellers = this.selectedSellers || [];
    const total = sellers.reduce((sum, seller) => sum + seller.weight, 0);
    return parseFloat(total.toFixed(2));
  }

  get availableCouriers() {
    return PickupDashboardController.availableCouriers.data;
  }

  get isLoadingCouriers() {
    return PickupDashboardController.isLoadingAvailableCouriers;
  }

  get eligibleCouriers() {
    return this.availableCouriers.filter(
      (courier) => courier.courierId !== (this.sourceCourierId || 0)
    );
  }

  get courierOptions() {
    return this.eligibleCouriers.map((courier) => ({
      name: `[${courier.courierId}] ${courier.courierName} (${courier.courierType}) ${courier.courierPhone}`,
      value: courier.courierId.toString(),
      courierId: courier.courierId
    }));
  }

  mounted() {
    this.taskGroupIdString;
    this.$watch("show", (newVal: boolean) => {
      if (newVal) {
        this.resetModal();
        this.fetchAvailableCouriers("", this.taskGroupIdString);
      }
    });
  }

  resetModal() {
    this.isAssigning = false;
    this.isShowModalSuccessBooking = false;
    this.isShowModalErrorBooking = false;
    this.openPartial = false;
    this.transferResult = null; // Reset transfer result
  }

  onCloseCourierSelect() {
    this.fetchAvailableCouriers("", this.taskGroupIdString);
  }

  get taskGroupIdString(): string | undefined {
    const sellers = this.selectedSellers || [];

    const taskGroupIds = sellers
      .map((seller: any) => seller.taskGroupId || seller.task_group_id) // Try both cases
      .filter((id) => id !== undefined && id !== null && id !== 0);

    return taskGroupIds.length > 0 ? taskGroupIds.join(",") : undefined;
  }

  // 1. Define the method normally so 'this' is bound correctly
  filterAvailableCouriers(search: string) {
    this.debouncedFetch(search, this.taskGroupIdString);
  }

  // 2. Create a debounced internal runner
  private debouncedFetch = debounce(
    async (search: string, groupId: string | undefined) => {
      await this.fetchAvailableCouriers(search, groupId);
    },
    400
  );

  async fetchAvailableCouriers(q = "", taskGroupIdString?: string) {
    try {
      await PickupDashboardController.getAvailableCouriers({
        taskGroupIds: taskGroupIdString,
        q: q
      });
    } catch (error) {
      console.error("Error fetching available couriers:", error);
    }
  }

  removeSeller(sellerIndex: number) {
    this.$emit("remove-seller", sellerIndex);
    this.fetchAvailableCouriers("", this.taskGroupIdString);
  }

  closeModal() {
    this.$emit("update:show", false);
    this.resetModal();
    this.fetchAvailableCouriers("", this.taskGroupIdString);
  }

  onSelectCourier(courier: any) {
    this.selectedCourier = courier;
    this.fetchAvailableCouriers("", this.taskGroupIdString);
  }

  onCloseSuccess() {
    this.isShowModalSuccessBooking = false;
    this.isShowModalErrorBooking = false;
    this.selectedCourier = "";
    // Emit success event to parent for refresh
    this.$emit("transfer-success");
  }

  prepareErrorAssignData(failedTransfers: any[]) {
    const filtered = this.filterValidFailedTransfers(failedTransfers);
    this.errorAssignData = filtered.map((failedItem: any) => ({
      senderName: failedItem.sender_name,
      senderPhone: failedItem.sender_phone,
      shipmentType: failedItem.shipment_type,
      totalShipment: failedItem.total_shipment,
      totalQuantity: failedItem.total_quantity,
      taskGroupId: failedItem.task_group_id
    }));
  }

  prepareErrorAllAssignData(failedTransfers: any[]) {
    const filtered = this.filterValidFailedTransfers(failedTransfers);
    this.errorAllAssignData = filtered.map((failedItem: any) => ({
      senderName: failedItem.sender_name,
      senderPhone: failedItem.sender_phone,
      shipmentType: failedItem.shipment_type,
      totalShipment: failedItem.total_shipment,
      totalQuantity: failedItem.total_quantity,
      taskGroupId: failedItem.task_group_id
    }));
  }

  filterValidFailedTransfers(items: any[]) {
    return items.filter((item: any) => this.isNonEmptyValue(item.sender_name));
  }

  isNonEmptyValue(v: any): boolean {
    if (v === null || v === undefined) return false;
    if (typeof v === "string") return v.trim() !== "";
    return String(v).trim() !== "";
  }

  onCloseModalAssign() {
    this.openPartial = false;
    this.selectedCourier = "";
    // Emit success event to parent for refresh
    this.$emit("transfer-success");
    this.errorAssignData = []; // Reset error data
  }

  onCloseModalErrorAllAssign() {
    this.openModalAllError = false;
    this.selectedCourier = "";
    // Emit success event to parent for refresh
    this.$emit("transfer-success");
    this.errorAllAssignData = []; // Reset error data
  }

  mapShipmentType(type: string): string {
    const typeMap: Record<string, string> = {
      AGGREGATOR: "Aggregator",
      MARKETPLACE: "Marketplace",
      CUSTOMER_APP: "Customer App",
      CORPORATE: "Corporate",
      PICKUP: "Pick Up"
    };
    return typeMap[type] || type;
  }

  fmtQty(val: number | string) {
    return formatByCountry(Number(val || 0), { type: "quantity" });
  }

  fmtWeight(val: number | string) {
    return formatByCountry(Number(val || 0), { type: "weight", suffix: " kg" });
  }

  async handleAssign() {
    const sellers = this.selectedSellers || [];
    this.isAssigning = true;

    try {
      const taskGroupIds = sellers
        .map((seller) => seller.taskGroupId)
        .filter((id) => id !== undefined && id !== null) as number[];

      // Create transfer request
      const transferRequest = new TransferPickupRequest({
        // eslint-disable-next-line @typescript-eslint/camelcase
        task_group_id: taskGroupIds,
        // eslint-disable-next-line @typescript-eslint/camelcase
        courier_id: parseInt(this.selectedCourier.value)
      });

      // Call transfer API through controller
      const result = await PickupDashboardController.transferPickup(
        transferRequest
      );
      this.transferResult = result;
      if (result.success) {
        const failedTransfers =
          result.data?.filter((item: any) => item.status === false) || [];
        if (failedTransfers.length > 0) {
          this.prepareErrorAssignData(failedTransfers);
          this.closeModal();
          this.openPartial = true;
        } else {
          this.closeModal();
          this.isShowModalSuccessBooking = true;
        }
      } else {
        const failedTransfers =
          result.data?.filter((item: any) => item.status === false) || [];
        if (failedTransfers.length > 0) {
          this.prepareErrorAllAssignData(failedTransfers);
          this.closeModal();
          this.openModalAllError = true;
        } else {
          this.isShowModalErrorBooking = true;
          this.closeModal();
        }
      }
    } catch (error) {
      this.isShowModalErrorBooking = true;
      console.error("Error during assignment:", error);
    } finally {
      this.isAssigning = false;
    }
  }
}
export default TransferPickupModal;
